import { createSelector } from '@reduxjs/toolkit';
import { INVALID_PARAM_ERROR } from 'constants/errors';
import {
  CHINESE_FIELD_ID,
  CHINESE_FIELD_NAME,
  PAN_NAME_FIELD,
  PAN_NUMBER_FIELD,
  PAYER_PASSPORT_FIELD_ID,
  SENDER_COUNTRY_FIELD,
  STUDENT_PASSPORT_FIELD_ID,
} from 'constants/fields';
import isEmpty from 'lodash/isEmpty';

import type { Field, FieldEntityType, FieldValue } from 'models/field';
import type { SectionField } from 'models/section';
import type { RootState } from 'reducers/types';
import { getCountry } from 'selectors/entities/countries/countries';
import {
  getOrder,
  isReadOnlyOrderSectionField,
} from 'selectors/entities/order/order';
import { SECTION_NAMES } from 'selectors/entities/sections/sections';
import {
  getFieldValue,
  isApiReadOnly,
  isPrefilled,
  isReadOnly,
} from 'selectors/ui/ui';

const CHINESE_FIELDS = [
  `offer_${CHINESE_FIELD_ID}`,
  `offer_${CHINESE_FIELD_NAME}`,
];
const INDIAN_FIELDS = [`offer_${PAN_NUMBER_FIELD}`, `offer_${PAN_NAME_FIELD}`];
const PASSPORT_FIELDS = [STUDENT_PASSPORT_FIELD_ID, PAYER_PASSPORT_FIELD_ID];

const getFieldError = (state: RootState, id: string) => {
  const { errors: { byParam = {} } = {} } = state;
  const { type, message } = byParam[id] || {};

  if (type !== INVALID_PARAM_ERROR) {
    return;
  }

  return message;
};

const getFieldText = (
  field: Field,
  defaultValue: FieldValue,
  state: RootState,
) => {
  const { value, type, format } = field;
  const fieldIsASelect = type === 'string' && Array.isArray(value);
  const fieldIsAMultiselect = type === 'array' && Array.isArray(value);
  const fieldIsACountry = format === 'country';

  switch (true) {
    case fieldIsASelect:
      const selectedValue = value.find(({ value }) => value === defaultValue);
      return selectedValue?.label;
    case fieldIsAMultiselect:
      const values = (defaultValue as string).split(',');
      const selectedValues = value.filter(({ value }) =>
        values.includes(value),
      );
      return selectedValues.map(v => v.label).join(', ');
    case fieldIsACountry:
      const country = getCountry(state, defaultValue as string);
      return country ? country.name : '';
    default:
      return defaultValue;
  }
};

export type FieldFromStore = ReturnType<typeof getFieldById>;

const getOrderReadOnlyFieldsMap = createSelector([getOrder], order => {
  if (!order) {
    return {};
  }

  const readOnlyFieldsMap: Record<string, boolean> = {};

  for (const sectionName of SECTION_NAMES) {
    // TODO this should be properly typed
    // biome-ignore lint/suspicious/noExplicitAny: false positive
    const section = (order as any)[sectionName];
    if (section?.fields && Array.isArray(section.fields)) {
      section.fields.forEach((field: SectionField) => {
        if (field && field.readOnly) {
          readOnlyFieldsMap[field.id] = true;
          readOnlyFieldsMap[`${sectionName}_${field.id}`] = true;
        }
      });
    }
  }

  return readOnlyFieldsMap;
});

const isOrderFieldReadOnly = (state: RootState, id: string): boolean => {
  if (isReadOnlyOrderSectionField(state, id)) {
    return true;
  }

  const readOnlyFieldsMap = getOrderReadOnlyFieldsMap(state);
  return !!readOnlyFieldsMap[id];
};

export const getFieldById = (state: RootState, id: string) => {
  const {
    entities: {
      fields: {
        sender: { byId: senderFieldsById } = {},
        offers: { byId: offerFieldsById } = {},
        recipient: { byId: recipientFieldsById } = {},
        academic: { byId: academicFieldsById } = {},
      },
    },
  } = state;
  const field =
    (senderFieldsById && senderFieldsById[id]) ||
    (offerFieldsById && offerFieldsById[id]) ||
    (recipientFieldsById && recipientFieldsById[id]) ||
    (academicFieldsById && academicFieldsById[id]);

  const defaultValue = getFieldValue(state, id);

  const error = getFieldError(state, id);
  const isValid = error == null;
  const apiReadOnly = isApiReadOnly(state, id);
  const isSenderCountryPrefilled =
    id === SENDER_COUNTRY_FIELD && !isEmpty(defaultValue);
  const prefilled = isPrefilled(state, id) || isSenderCountryPrefilled;
  const text = field ? getFieldText(field, defaultValue, state) : '';
  const orderFieldReadOnly = isOrderFieldReadOnly(state, id);
  const readOnly = isReadOnly(state, id) || apiReadOnly || orderFieldReadOnly;

  return {
    id,
    ...(field || {}),
    defaultValue:
      field?.type === 'boolean'
        ? getBooleanDefaultValue(defaultValue)
        : defaultValue,
    error,
    isValid,
    readOnly,
    apiReadOnly,
    prefilled,
    text,
    value: field?.value !== null ? field?.value : undefined,
    autoComplete:
      field?.autoComplete !== null ? field?.autoComplete : undefined,
    format: field?.format !== null ? field?.format : undefined,
    help: field?.help !== null ? field?.help : undefined,
    hidden: field?.hidden !== null ? field?.hidden : undefined,
    hint: field?.hint !== null ? field?.hint : undefined,
    label: field?.label !== null ? field?.label : undefined,
    name: field?.name !== null ? field?.name : undefined,
    pattern: field?.pattern !== null ? field?.pattern : undefined,
    patternMessage:
      field?.patternMessage !== null ? field?.patternMessage : undefined,
    placeholder: field?.placeholder !== null ? field?.placeholder : undefined,
    position: field?.position !== null ? field?.position : undefined,
    required: field?.required !== null ? field?.required : undefined,
    section: field?.section !== null ? field?.section : undefined,
    type: field?.type !== null ? field?.type : undefined,
  };
};

export const getFieldByEntity = (state: RootState, entity: FieldEntityType) => {
  const { entities: { fields } = {} } = state;

  return fields?.[entity] || { ids: [], byId: {} };
};

export const getHiddenFieldIds = (state: RootState) => {
  const { byId: senderFieldsById } = getFieldByEntity(state, 'offers') || {};
  const { byId: offerFieldsById } = getFieldByEntity(state, 'sender') || {};
  const { byId: recipientFieldsById } =
    getFieldByEntity(state, 'recipient') || {};
  const { byId: academicFieldsById } =
    getFieldByEntity(state, 'academic') || {};

  const fields = {
    ...offerFieldsById,
    ...senderFieldsById,
    ...recipientFieldsById,
    ...academicFieldsById,
  };
  const fieldIds = Object.keys(fields);

  const hiddenFieldIds = fieldIds.reduce((acc, id) => {
    if (fields[id].hidden) {
      return [...acc, id];
    }
    return acc;
  }, [] as string[]);

  return hiddenFieldIds;
};

export const getKYCFields = (state: RootState) => {
  const entity = getFieldByEntity(state, 'kycs');
  const ids = entity?.ids;

  return ids?.map(id => entity?.byId[id]) || [];
};

export const hasChineseFields = (state: RootState) =>
  hasOfferFields(CHINESE_FIELDS, state);
export const hasIndianFields = (state: RootState) =>
  hasOfferFields(INDIAN_FIELDS, state);
export const hasPassportFields = (state: RootState) =>
  hasRecipientFields(PASSPORT_FIELDS, state);

const hasOfferFields = (fieldsToSearch: string[], state: RootState) => {
  return hasEntityFields(state, 'offers', fieldsToSearch);
};

const hasRecipientFields = (fieldsToSearch: string[], state: RootState) => {
  return hasEntityFields(state, 'recipient', fieldsToSearch);
};

const hasEntityFields = (
  state: RootState,
  entity: FieldEntityType,
  fieldsToSearch: string[],
) => {
  const { ids = [] } = getFieldByEntity(state, entity);
  const isFound = (field: string) => fieldsToSearch.includes(field);

  return ids.some(isFound);
};

const getBooleanDefaultValue = (value: string | boolean | number) => {
  return typeof value === 'boolean' ? value : value === 'true';
};
